import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { reviewQueue, hl7Verwerfen, type ReviewQueueItem } from '../api/unimedica';
import StatusBadge from '../components/StatusBadge';
import Hl7Zuordnen from '../components/Hl7Zuordnen';

// Port aus routes/eingang/+page.svelte. Gruppierung nach powerCONTACT-Muster:
// „Ohne Patient" immer zuoberst (rot) — das ist das Dringende.

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function patientName(item: ReviewQueueItem): string {
  const r = item.recognized ?? {};
  const m = (r.patient_meta ?? {}) as Record<string, string>;
  return [r.vorname ?? m.vorname, r.nachname ?? m.nachname].filter(Boolean).join(' ');
}
function hasPatient(item: ReviewQueueItem): boolean {
  if (patientName(item) !== '') return true;
  const rest = (item.title ?? '').split('·').slice(1).join('·').trim();
  return rest !== '' && rest !== 'Patient?';
}
function docTyp(item: ReviewQueueItem): string {
  const r = item.recognized ?? {};
  if (r.typ) return cap(String(r.typ));
  const head = (item.title ?? '').split('·')[0].trim();
  return head ? cap(head) : 'Dokument';
}
// PATIENT ZUERST (Verwechslungsschutz): Name + geb. als erstes Element.
function patientLabel(item: ReviewQueueItem): { text: string; unknown: boolean } {
  const name = patientName(item);
  const r = item.recognized ?? {};
  const geb = r.geburtsdatum ?? (r.patient_meta ?? {}).geburtsdatum;
  if (name) return { text: geb ? `${name} · geb. ${geb}` : name, unknown: false };
  const rest = (item.title ?? '').split('·').slice(1).join('·').trim();
  if (rest && rest !== 'Patient?') return { text: rest.replace(/^X+_/i, '').replace(/_/g, ' '), unknown: false };
  return { text: 'Patient unbekannt', unknown: true };
}
function metaParts(item: ReviewQueueItem): string[] {
  const r = item.recognized ?? {};
  const out: string[] = [];
  if (r.geburtsdatum && !patientName(item)) out.push(`geb. ${r.geburtsdatum}`);
  if (r.datum) out.push(String(r.datum));
  out.push(item.source === 'inbox' ? 'aus Mail-Import' : 'Zuordnung prüfen');
  return out;
}
const isNeu = (i: ReviewQueueItem) => i.source === 'inbox';
// Laborbefunde ohne Zuordnung (HL7): kein Dokument zum Prüfen, sondern eine offene Frage —
// welcher Patient? Deshalb eigene Zeile mit den Daten aus der Nachricht und eigener Aktion.
const isHl7 = (i: ReviewQueueItem) => i.source === 'hl7';
function hl7Meta(item: ReviewQueueItem): string[] {
  const r = item.recognized ?? {};
  const out: string[] = [];
  if (r.datum) out.push(`Befund ${r.datum}`);
  if (r.laborwerte) out.push(`${r.laborwerte} Laborwerte`);
  if (r.absender) out.push(String(r.absender));
  if (r.geburtsdatum) out.push(`geb. ${r.geburtsdatum}`);
  return out;
}

/* Womit der Befund ueberschrieben wird.
 *
 * Die Laborgeraete senden keinen Namen — nur die Nummer, die am Geraet eingetippt wurde.
 * Genau die gehoert nach vorn; "Name unbekannt" stand zwar da, half aber niemandem. */
function hl7Titel(item: ReviewQueueItem): string {
  const r = item.recognized ?? {};
  const name = [r.vorname, r.nachname].filter(Boolean).join(' ');
  if (name) return name;
  const k = (r.kennungen ?? []) as string[];
  if (k.length) return `Patientennummer ${k.join(', ')}`;
  return 'Ohne Patientennummer';
}

/* Die ersten Werte als Merkhilfe — daran erkennt die Praxis den Fall. */
function hl7Werte(item: ReviewQueueItem): string {
  const w = ((item.recognized ?? {}).werte ?? []) as any[];
  if (!w.length) return '';
  return w.slice(0, 5)
    .map((v) => `${v.parameter} ${v.wert}${v.einheit ? ' ' + v.einheit : ''}`)
    .join(' · ') + (w.length > 5 ? ' …' : '');
}

type View = 'alle' | 'neu' | 'korrektur';
type Group = { label: string; err: boolean; rows: ReviewQueueItem[] };

export default function Eingang() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<string | null>(null);
  const [view, setView] = useState<View>('alle');
  const [zuordnen, setZuordnen] = useState<ReviewQueueItem | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = () => reviewQueue().then((q) => setItems(q.items)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    laden();
    const t = setInterval(laden, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verwerfen = async (item: ReviewQueueItem) => {
    if (!window.confirm('Diesen Laborbefund aus dem Eingang nehmen? Er wird keiner Akte zugeordnet.')) return;
    try { await hl7Verwerfen(item.ref); setMeldung('Befund aus dem Eingang genommen.'); await laden(); }
    catch (e: any) { setMeldung(e?.message ?? String(e)); }
  };

  const review = (item: ReviewQueueItem) => {
    setOpening(item.ref);
    navigate(`/eingang/pruefen?ref=${encodeURIComponent(item.ref)}`);
  };

  const neuCount = items.filter(isNeu).length;
  const korrCount = items.length - neuCount;

  const groups = useMemo<Group[]>(() => {
    let list = items;
    if (view === 'neu') list = list.filter(isNeu);
    if (view === 'korrektur') list = list.filter((i) => !isNeu(i));
    const hl7 = list.filter(isHl7);
    const uebrig = list.filter((i) => !isHl7(i));
    const ohne = uebrig.filter((i) => !hasPatient(i));
    const rest = uebrig.filter(hasPatient);
    const g: Group[] = [];
    if (hl7.length) g.push({ label: 'Laborbefunde ohne Zuordnung', err: true, rows: hl7 });
    if (ohne.length) g.push({ label: 'Ohne Patient — Zuordnung nötig', err: true, rows: ohne });
    if (view === 'alle') {
      const neu = rest.filter(isNeu), korr = rest.filter((i) => !isNeu(i));
      if (neu.length) g.push({ label: 'Neu', err: false, rows: neu });
      if (korr.length) g.push({ label: 'Korrektur', err: false, rows: korr });
    } else if (rest.length) {
      g.push({ label: view === 'neu' ? 'Neu' : 'Korrektur', err: false, rows: rest });
    }
    return g;
  }, [items, view]);

  /* Wenn Befunde liegenbleiben, liegt es fast immer an der Patientenliste: sie ist ein
   * Auszug aus siMed und kennt niemanden, der danach neu angelegt wurde. Das gehoert nach
   * oben, sonst sucht die Praxis im Zuordnen-Dialog nach einem Namen, den es dort nie gibt. */
  const listenHinweis = useMemo(() => {
    const hl7 = items.filter(isHl7);
    if (!hl7.length) return null;
    const stand = String((hl7[0].recognized ?? {}).stammliste_stand ?? '');
    if (!/^\d{2}\.\d{2}\.\d{4}$/.test(stand)) return null;
    const [d, m, y] = stand.split('.').map(Number);
    const tage = Math.floor((Date.now() - new Date(y, m - 1, d).getTime()) / 86400000);
    if (tage < 8) return null;
    return { stand, tage };
  }, [items]);

  const seg = (v: View, label: string, n?: number) => (
    <button onClick={() => setView(v)} className={`seg-btn min-h-11 ${view === v ? 'seg-active' : ''}`}>
      {label} {n ? <span className="numerical">({n})</span> : null}
    </button>
  );

  return (
    <div className="space-y-4">
      {meldung && (
        <div className="flex items-start justify-between gap-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <span>{meldung}</span>
          <button onClick={() => setMeldung(null)} className="text-xs underline">schliessen</button>
        </div>
      )}
      {listenHinweis && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="font-medium">Die Patientenliste ist vom {listenHinweis.stand}</span>
          {' '}({listenHinweis.tage} Tage alt). Patienten, die seither in siMed angelegt wurden, stehen
          noch nicht darin — deren Befunde lassen sich hier nicht zuordnen, bis die Liste erneuert ist.
        </div>
      )}
      {zuordnen && (
        <Hl7Zuordnen item={zuordnen} onClose={() => setZuordnen(null)}
          onFertig={(m) => { setZuordnen(null); setMeldung(m); void laden(); }} />
      )}
      <div className="card">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h1 className="page-h1">
            {items.length === 0 ? 'Nichts zu prüfen' : items.length === 1 ? 'Ein Eintrag wartet auf Sie' : `${items.length} Einträge warten auf Sie`}
          </h1>
          <div className="seg">
            {seg('alle', 'Alle')}
            {seg('neu', 'Zu prüfen', neuCount)}
            {seg('korrektur', 'Zu korrigieren', korrCount)}
          </div>
        </div>

        {loading ? (
          <div className="divide-y divide-gray-200">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-1 py-3">
                <div className="shimmer h-8 w-8 shrink-0 rounded" />
                <div className="flex-1 space-y-1.5"><div className="shimmer h-3 w-1/3 rounded" /><div className="shimmer h-2.5 w-1/2 rounded" /></div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50"><CheckIcon className="h-5 w-5 text-emerald-700" /></div>
            <div className="mb-0.5 text-sm font-medium text-ps-dark-700">Nichts zu tun</div>
            <p className="text-xs text-ps-dark-400">Alle eingegangenen Dokumente sind geprüft und in den Akten abgelegt.</p>
          </div>
        ) : (
          <div className="-mx-4 divide-y divide-gray-200">
            {groups.map((group) => (
              <div key={group.label}>
                <div className={`group-head px-4 ${group.err ? 'group-head-err' : ''}`}>{group.label} <span className="opacity-60">· {group.rows.length}</span></div>
                {group.rows.map((item) => {
                  if (isHl7(item)) {
                    const r = item.recognized ?? {};
                    const name = hl7Titel(item);
                    const werte = hl7Werte(item);
                    return (
                      <div key={item.ref} className="flex min-h-14 w-full items-center gap-3 border-l-2 border-l-amber-500 bg-amber-50/40 px-4 py-3">
                        <StatusBadge variant="needs_correction" label="zuordnen" size="sm" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm">
                            <span className="font-semibold text-ps-dark-900">{name}</span>
                            <span className="text-ps-dark-500">
                              {' '}— {r.ohne_kennung
                                ? 'am Gerät wurde keine Patientennummer eingegeben'
                                : 'Nummer passt zu keiner Akte'}
                            </span>
                          </div>
                          <div className="truncate text-xs text-ps-dark-400">{hl7Meta(item).join(' · ')}</div>
                          {werte && <div className="truncate text-xs text-ps-dark-500">{werte}</div>}
                        </div>
                        <button onClick={() => void verwerfen(item)} className="btn-ghost shrink-0 text-xs">Verwerfen</button>
                        <button onClick={() => setZuordnen(item)} className="btn-primary shrink-0 text-xs">Zuordnen</button>
                      </div>
                    );
                  }
                  const p = patientLabel(item);
                  return (
                    <button key={item.ref} onClick={() => review(item)} disabled={opening !== null} title={item.title}
                      className={`flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 disabled:opacity-60 ${group.err ? 'border-l-2 border-l-rose-500 bg-rose-50/45' : ''}`}>
                      <StatusBadge variant={isNeu(item) ? 'pending' : 'needs_correction'} label={isNeu(item) ? 'zu prüfen' : 'korrigieren'} size="sm" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm">
                          <span className={`font-semibold ${p.unknown ? 'text-rose-700' : 'text-ps-dark-900'}`}>{p.text}</span>
                          <span className="text-ps-dark-500"> — {docTyp(item)}</span>
                        </div>
                        <div className="truncate text-xs text-ps-dark-400">{metaParts(item).join(' · ')}</div>
                      </div>
                      {opening === item.ref
                        ? <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary-300 border-t-primary-500" />
                        : <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-primary-500">Prüfen <ArrowRightIcon className="h-3 w-3" /></span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
